<template>
    <div class="debug-container">
        <h1 class="text-h3 text-primary">🎼 Music Phrase Component Demo</h1>

        <div class="layout">
            <!-- Left Panel: Controls -->
            <div class="controls-panel bg-surface">
                <h2 class="text-h5 text-secondary">Configuration</h2>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Notes Editor</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label>Notes in Phrase:</label>
                            <div class="notes-editor">
                                <div
                                    v-for="(note, index) in phraseNotes"
                                    :key="index"
                                    class="note-row"
                                >
                                    <input
                                        :value="midiToNote(note.midi)"
                                        @input="updateNoteFromString(index, ($event.target as HTMLInputElement).value)"
                                        type="text"
                                        placeholder="Note (e.g., C5)"
                                        class="note-input"
                                    />
                                    <input
                                        v-model.number="note.midi"
                                        type="number"
                                        min="0"
                                        max="127"
                                        class="midi-input"
                                        title="MIDI number"
                                    />
                                    <select
                                        v-model="note.duration"
                                        class="duration-select"
                                    >
                                        <option value="whole">Whole</option>
                                        <option value="half">Half</option>
                                        <option value="quarter">Quarter</option>
                                        <option value="eighth">Eighth</option>
                                        <option value="sixteenth">16th</option>
                                    </select>
                                    <button
                                        @click="removeNote(index)"
                                        class="remove-btn"
                                        :disabled="phraseNotes.length <= 1"
                                    >
                                        ✕
                                    </button>
                                </div>
                            </div>
                            <button @click="addNote" class="add-note-btn">
                                + Add Note
                            </button>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Note Decorations</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label for="highlight-index">
                                Highlight Note Index ({{
                                    highlightIndex >= 0
                                        ? highlightIndex
                                        : "none"
                                }}):
                            </label>
                            <input
                                id="highlight-index"
                                type="range"
                                v-model.number="highlightIndex"
                                :min="-1"
                                :max="phraseNotes.length - 1"
                                step="1"
                            />
                            <div
                                class="note-hint text-caption text-medium-emphasis"
                            >
                                -1 = no highlight, use for "current note"
                                playback
                            </div>
                        </div>

                        <div class="property-control">
                            <label for="incorrect-index">
                                Mark as Incorrect ({{
                                    incorrectIndex >= 0
                                        ? incorrectIndex
                                        : "none"
                                }}):
                            </label>
                            <input
                                id="incorrect-index"
                                type="range"
                                v-model.number="incorrectIndex"
                                :min="-1"
                                :max="phraseNotes.length - 1"
                                step="1"
                            />
                            <div
                                class="note-hint text-caption text-medium-emphasis"
                            >
                                -1 = none, use for wrong note feedback
                            </div>
                        </div>

                        <div class="property-control">
                            <label for="mute-past-notes">
                                Mute Past Notes (opacity {{ mutedOpacity }}):
                            </label>
                            <input
                                id="mute-past-notes"
                                type="range"
                                v-model.number="mutedOpacity"
                                min="0"
                                max="1"
                                step="0.1"
                            />
                            <div
                                class="note-hint text-caption text-medium-emphasis"
                            >
                                Makes notes before highlighted note
                                semi-transparent
                            </div>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Pitch Feedback Overlay</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label class="checkbox-label">
                                <input
                                    type="checkbox"
                                    v-model="overlayEnabled"
                                />
                                Enable overlay (simulate pitch feedback)
                            </label>
                        </div>
                        <div class="property-control">
                            <label for="overlay-note-index">
                                Overlay on Note Index ({{
                                    overlayNoteIndex
                                }}):
                            </label>
                            <input
                                id="overlay-note-index"
                                type="range"
                                v-model.number="overlayNoteIndex"
                                :min="0"
                                :max="phraseNotes.length - 1"
                                step="1"
                                :disabled="!overlayEnabled"
                            />
                            <div
                                class="note-hint text-caption text-medium-emphasis"
                            >
                                Which goal note to overlay on
                            </div>
                        </div>

                        <div class="property-control">
                            <label for="overlay-midi">
                                Played Note MIDI ({{
                                    overlayMidi
                                }} = {{ midiToNote(overlayMidi) }}):
                            </label>
                            <input
                                id="overlay-midi"
                                type="range"
                                v-model.number="overlayMidi"
                                :min="60"
                                :max="84"
                                step="1"
                                :disabled="!overlayEnabled"
                            />
                            <div
                                class="note-hint text-caption text-medium-emphasis"
                            >
                                The note the user actually played
                            </div>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Fingering Options</h3>
                    <div class="property-group">
                        <div class="property-control">
                            <label class="checkbox-label">
                                <input
                                    type="checkbox"
                                    v-model="showFingerings"
                                />
                                Show fingering diagrams
                            </label>
                        </div>
                        <div class="property-control">
                            <label class="checkbox-label">
                                <input
                                    type="checkbox"
                                    v-model="showFingeringLabels"
                                    :disabled="!showFingerings"
                                />
                                Show note labels on fingerings
                            </label>
                        </div>
                        <div class="property-control">
                            <label class="checkbox-label">
                                <input
                                    type="checkbox"
                                    v-model="skipRepeatingFingerings"
                                    :disabled="!showFingerings"
                                />
                                Skip repeating fingerings
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Center Panel: Preview -->
            <div class="preview-panel bg-surface">
                <h2 class="text-h5 text-secondary">Preview</h2>
                <div class="preview-box">
                    <MusicPhrase
                        :notes="decoratedNotes"
                        :clef="clef"
                        :key-signature="keySignature"
                        :width="staffWidth"
                        :height="staffHeight"
                        :show-fingerings="showFingerings"
                        :show-fingering-labels="showFingeringLabels"
                        :skip-repeating-fingerings="skipRepeatingFingerings"
                        :fingering-width="fingeringWidth"
                        :fingering-height="fingeringHeight"
                        :time-signature="timeSignature"
                        :enforce-time-signature="enforceTimeSignature"
                        :overlay="noteOverlay"
                    />
                </div>
            </div>

            <!-- Right Panel: Size Controls & Info -->
            <div class="right-panel bg-surface">
                <h2 class="text-h5 text-secondary">Staff Configuration</h2>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Quick Presets</h3>
                    <div class="property-group">
                        <div class="button-group-vertical">
                            <button
                                v-for="preset in phrasePresets"
                                :key="preset.name"
                                @click="loadPreset(preset)"
                                class="state-btn"
                            >
                                {{ preset.name }}
                            </button>
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Clef</h3>
                    <div class="button-group">
                        <button
                            @click="clef = 'treble'"
                            :class="{ active: clef === 'treble' }"
                            class="state-btn"
                        >
                            Treble
                        </button>
                        <button
                            @click="clef = 'bass'"
                            :class="{ active: clef === 'bass' }"
                            class="state-btn"
                        >
                            Bass
                        </button>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Key Signature</h3>
                    <div class="button-group">
                        <button
                            @click="keySignature = undefined"
                            :class="{ active: keySignature === undefined }"
                            class="state-btn"
                        >
                            None
                        </button>
                        <button
                            @click="keySignature = 'C'"
                            :class="{ active: keySignature === 'C' }"
                            class="state-btn"
                        >
                            C
                        </button>
                        <button
                            @click="keySignature = 'G'"
                            :class="{ active: keySignature === 'G' }"
                            class="state-btn"
                        >
                            G
                        </button>
                        <button
                            @click="keySignature = 'D'"
                            :class="{ active: keySignature === 'D' }"
                            class="state-btn"
                        >
                            D
                        </button>
                        <button
                            @click="keySignature = 'F'"
                            :class="{ active: keySignature === 'F' }"
                            class="state-btn"
                        >
                            F
                        </button>
                        <button
                            @click="keySignature = 'Bb'"
                            :class="{ active: keySignature === 'Bb' }"
                            class="state-btn"
                        >
                            Bb
                        </button>
                        <button
                            @click="keySignature = 'A'"
                            :class="{ active: keySignature === 'A' }"
                            class="state-btn"
                        >
                            A
                        </button>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Time Signature</h3>
                    <div class="button-group">
                        <button
                            @click="timeSignature = '4/4'"
                            :class="{ active: timeSignature === '4/4' }"
                            class="state-btn"
                        >
                            4/4
                        </button>
                        <button
                            @click="timeSignature = '3/4'"
                            :class="{ active: timeSignature === '3/4' }"
                            class="state-btn"
                        >
                            3/4
                        </button>
                        <button
                            @click="timeSignature = '6/8'"
                            :class="{ active: timeSignature === '6/8' }"
                            class="state-btn"
                        >
                            6/8
                        </button>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Timing Mode</h3>
                    <div class="property-control">
                        <label class="checkbox-label">
                            <input
                                type="checkbox"
                                v-model="enforceTimeSignature"
                            />
                            Enforce time signature
                        </label>
                        <div
                            class="note-hint text-caption text-medium-emphasis"
                        >
                            Disable for "free timing" mode
                        </div>
                    </div>
                </div>

                <div class="property-section bg-surface-light">
                    <h3 class="text-h6">Staff Sizing</h3>

                    <div class="size-control-group">
                        <label for="staff-width-slider"
                            >Width: {{ staffWidth }}px</label
                        >
                        <input
                            id="staff-width-slider"
                            type="range"
                            v-model.number="staffWidth"
                            min="400"
                            max="1200"
                            step="50"
                        />
                    </div>

                    <div class="size-control-group">
                        <label for="staff-height-slider"
                            >Height: {{ staffHeight }}px</label
                        >
                        <input
                            id="staff-height-slider"
                            type="range"
                            v-model.number="staffHeight"
                            min="150"
                            max="400"
                            step="25"
                        />
                    </div>

                    <h3 class="text-h6">Fingerings</h3>

                    <div class="size-control-group">
                        <label for="fingering-width-slider"
                            >Width: {{ fingeringWidth }}px</label
                        >
                        <input
                            id="fingering-width-slider"
                            type="range"
                            v-model.number="fingeringWidth"
                            min="60"
                            max="150"
                            step="10"
                        />
                    </div>

                    <div class="size-control-group">
                        <label for="fingering-height-slider"
                            >Height: {{ fingeringHeight }}px</label
                        >
                        <input
                            id="fingering-height-slider"
                            type="range"
                            v-model.number="fingeringHeight"
                            min="80"
                            max="200"
                            step="10"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- Usage Instructions -->
        <v-card class="instructions-section">
            <v-card-title class="text-h5 text-secondary">
                Features & Usage
            </v-card-title>
            <v-card-text>
                <h3 class="text-h6 mb-3">Core Features</h3>
                <ul class="usage-list">
                    <li>
                        <strong>Musical Phrases:</strong> Display multiple notes
                        in sequence on a staff
                    </li>
                    <li>
                        <strong>Fingering Diagrams:</strong> Optional vertical
                        fingering diagrams aligned with each note
                    </li>
                    <li>
                        <strong>Smart Repetition:</strong> When enabled,
                        fingering diagrams don't repeat when the same note
                        appears consecutively
                    </li>
                    <li>
                        <strong>Flexible Layout:</strong> Position fingerings
                        above or below the staff
                    </li>
                    <li>
                        <strong>Multiple Durations:</strong> Mix whole, half,
                        quarter, eighth, and sixteenth notes
                    </li>
                    <li>
                        <strong>Accidentals:</strong> Supports sharps (#) and
                        flats (b) - e.g., "F#5", "Bb4"
                    </li>
                    <li>
                        <strong>Free Timing Mode:</strong> Disable time
                        signature enforcement to render any number of notes
                        without barlines or time signature restrictions
                    </li>
                </ul>

                <h3 class="text-h6 mt-6 mb-3">Note Decorations</h3>
                <ul class="usage-list">
                    <li>
                        <strong>Highlight Current Note:</strong> Use the slider
                        to highlight a specific note (with blue glow effect).
                        Perfect for showing which note should be played during
                        playback.
                    </li>
                    <li>
                        <strong>Mark Incorrect Notes:</strong> Use the slider to
                        mark a note as incorrect (displays in red). Useful for
                        pitch detection feedback when user plays wrong note.
                    </li>
                    <li>
                        <strong>Mute Past Notes:</strong> Adjust opacity of
                        notes before the highlighted note. Creates a
                        "follow-along" effect where completed notes fade into
                        the background.
                    </li>
                    <li>
                        <strong>Integration Ready:</strong> These decorations
                        are designed to integrate with pitch detection for
                        real-time practice feedback.
                    </li>
                </ul>

                <h3 class="text-h6 mt-6 mb-3">Free Timing Mode</h3>
                <ul class="usage-list">
                    <li>
                        <strong>No Time Signature Restrictions:</strong> When
                        <code>enforceTimeSignature</code> is false, you can pass
                        any number of notes with any durations without worrying
                        about mathematical time signature compatibility.
                    </li>
                    <li>
                        <strong>Auto-Width Adjustment:</strong> The staff
                        automatically expands to accommodate all notes, ensuring
                        readability even with many notes.
                    </li>
                    <li>
                        <strong>Clean Appearance:</strong> No time signature or
                        barlines are displayed, creating a clean "stream of
                        notes" appearance perfect for exercises and practice
                        sequences.
                    </li>
                    <li>
                        <strong>Perfect for Exercises:</strong> Ideal for
                        scales, arpeggios, technical exercises, and any musical
                        content where strict time signatures aren't necessary.
                    </li>
                </ul>

                <div class="demo-tip">
                    <strong>💡 Try it:</strong> Load the "Free Timing: 15
                    Quarter Notes" preset to see free timing mode in action, or
                    "Playback Demo (Highlighted)" to see note decorations!
                </div>
            </v-card-text>
        </v-card>

        <!-- Code Output -->
        <v-card class="code-section">
            <v-card-title>
                <div class="code-header">
                    <h2 class="text-h6">Component Code</h2>
                    <v-btn @click="copyCode" size="small" variant="outlined">
                        📋 Copy
                    </v-btn>
                </div>
            </v-card-title>
            <v-card-text>
                <pre><code>{{ componentCode }}</code></pre>
            </v-card-text>
        </v-card>

        <!-- API Documentation -->
        <v-card class="api-section">
            <v-card-title class="text-h5 text-secondary">
                API Documentation
            </v-card-title>
            <v-card-text>
                <h3 class="text-h6 mb-3">Props</h3>
                <v-table>
                    <thead>
                        <tr>
                            <th>Prop</th>
                            <th>Type</th>
                            <th>Default</th>
                            <th>Description</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td><code>notes</code></td>
                            <td><code>NoteData[]</code></td>
                            <td>Sample scale</td>
                            <td>Array of note objects to display</td>
                        </tr>
                        <tr>
                            <td><code>clef</code></td>
                            <td><code>string</code></td>
                            <td>"treble"</td>
                            <td>Musical clef (treble, bass, alto, tenor)</td>
                        </tr>
                        <tr>
                            <td><code>width</code></td>
                            <td><code>number</code></td>
                            <td>800</td>
                            <td>Width of the musical staff in pixels</td>
                        </tr>
                        <tr>
                            <td><code>height</code></td>
                            <td><code>number</code></td>
                            <td>200</td>
                            <td>Height of the musical staff in pixels</td>
                        </tr>
                        <tr>
                            <td><code>showFingerings</code></td>
                            <td><code>boolean</code></td>
                            <td>true</td>
                            <td>Display fingering diagrams with notes</td>
                        </tr>
                        <tr>
                            <td><code>showFingeringLabels</code></td>
                            <td><code>boolean</code></td>
                            <td>false</td>
                            <td>Show note names on fingering diagrams</td>
                        </tr>
                        <tr>
                            <td><code>skipRepeatingFingerings</code></td>
                            <td><code>boolean</code></td>
                            <td>true</td>
                            <td>
                                Hide fingerings when note repeats consecutively
                            </td>
                        </tr>
                        <tr>
                            <td><code>fingeringPosition</code></td>
                            <td><code>"above" | "below"</code></td>
                            <td>"below"</td>
                            <td>
                                Position of fingering diagrams relative to staff
                            </td>
                        </tr>
                        <tr>
                            <td><code>fingeringWidth</code></td>
                            <td><code>number</code></td>
                            <td>60</td>
                            <td>Width of fingering diagrams in pixels</td>
                        </tr>
                        <tr>
                            <td><code>fingeringHeight</code></td>
                            <td><code>number</code></td>
                            <td>180</td>
                            <td>Height of fingering diagrams in pixels</td>
                        </tr>
                        <tr>
                            <td><code>timeSignature</code></td>
                            <td><code>string</code></td>
                            <td>"4/4"</td>
                            <td>Time signature (e.g., "4/4", "3/4", "6/8")</td>
                        </tr>
                        <tr>
                            <td><code>enforceTimeSignature</code></td>
                            <td><code>boolean</code></td>
                            <td>true</td>
                            <td>
                                When false, renders notes without barlines in
                                "free timing" mode
                            </td>
                        </tr>
                    </tbody>
                </v-table>

                <h3 class="text-h6 mt-6 mb-3">NoteData Type</h3>
                <pre class="type-definition"><code>{
  note: string;           // Note name (e.g., "C5", "D#5", "Bb4")
  duration?: string;      // Duration (whole, half, quarter, eighth, sixteenth)
  fingering?: FingeringState; // Optional custom fingering override
  color?: string;         // Custom color (hex or name: "#ff0000", "red")
  opacity?: number;       // Opacity 0-1 (for muted/past notes)
  highlight?: boolean;    // Highlight (e.g., current note in playback)
  incorrect?: boolean;    // Mark as incorrect (for user feedback)
}</code></pre>

                <h3 class="text-h6 mt-6 mb-3">Features</h3>
                <ul class="api-list">
                    <li>
                        <strong>Multi-note display:</strong> Render complete
                        musical phrases on a staff
                    </li>
                    <li>
                        <strong>Vertical fingerings:</strong> Optional fingering
                        diagrams aligned with each note
                    </li>
                    <li>
                        <strong>Smart repetition:</strong> Optionally skip
                        fingerings for repeated notes
                    </li>
                    <li>
                        <strong>Flexible positioning:</strong> Place fingerings
                        above or below the staff
                    </li>
                    <li>
                        <strong>Accidental support:</strong> Handles sharps (#)
                        and flats (b)
                    </li>
                    <li>
                        <strong>Mixed durations:</strong> Combine different note
                        values in one phrase
                    </li>
                    <li>
                        <strong>VexFlow powered:</strong> Professional music
                        notation rendering
                    </li>
                    <li>
                        <strong>Note decorations:</strong> Color, highlight, and
                        mark notes as incorrect
                    </li>
                    <li>
                        <strong>Playback integration:</strong> Highlight current
                        note with glow effect during playback
                    </li>
                    <li>
                        <strong>Error feedback:</strong> Visual feedback for
                        incorrect notes (pitch detection integration)
                    </li>
                    <li>
                        <strong>Opacity control:</strong> Mute past notes for
                        "follow-along" style practice
                    </li>
                </ul>

                <h3 class="text-h6 mt-6 mb-3">Decoration Use Cases</h3>
                <ul class="api-list">
                    <li>
                        <strong>Playback tracking:</strong> Use
                        <code>highlight</code> to show current note being played
                    </li>
                    <li>
                        <strong>Error feedback:</strong> Use
                        <code>incorrect</code> to mark wrong notes during
                        practice
                    </li>
                    <li>
                        <strong>Progressive reveal:</strong> Use
                        <code>opacity</code> to fade out completed notes
                    </li>
                    <li>
                        <strong>Custom styling:</strong> Use
                        <code>color</code> to theme notes or indicate difficulty
                    </li>
                </ul>

                <h3 class="text-h6 mt-6 mb-3">Basic Usage Example</h3>
                <pre class="usage-example"><code>&lt;MusicPhrase
  :notes="[
    { note: 'C5', duration: 'quarter' },
    { note: 'D5', duration: 'quarter' },
    { note: 'E5', duration: 'half' },
  ]"
  :show-fingerings="true"
  :skip-repeating-fingerings="true"
  time-signature="4/4"
/&gt;</code></pre>

                <h3 class="text-h6 mt-6 mb-3">Playback Integration Example</h3>
                <pre class="usage-example"><code>&lt;MusicPhrase
  :notes="[
    { note: 'C5', duration: 'quarter', opacity: 0.3 },    // Past note (muted)
    { note: 'D5', duration: 'quarter', opacity: 0.3 },    // Past note (muted)
    { note: 'E5', duration: 'quarter', highlight: true }, // Current note (highlighted)
    { note: 'F5', duration: 'quarter' },                  // Future note
    { note: 'G5', duration: 'quarter' },                  // Future note
  ]"
  :show-fingerings="true"
  time-signature="4/4"
/&gt;</code></pre>

                <h3 class="text-h6 mt-6 mb-3">Error Feedback Example</h3>
                <pre class="usage-example"><code>&lt;MusicPhrase
  :notes="[
    { note: 'C5', duration: 'quarter' },
    { note: 'D5', duration: 'quarter', incorrect: true }, // User played wrong note
    { note: 'E5', duration: 'quarter' },
  ]"
  :show-fingerings="true"
  time-signature="4/4"
/&gt;</code></pre>
            </v-card-text>
        </v-card>
    </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import MusicPhrase, { type NoteData, type NoteOverlay } from "../components/MusicPhrase.vue";
import { noteToMidi, midiToNote } from "../data/fluteFingeringChart";

interface PhrasePreset {
    name: string;
    notes: NoteData[];
    timeSignature?: string;
    enforceTimeSignature?: boolean;
}

const phraseNotes = ref<NoteData[]>([
    { midi: 72, duration: "quarter" }, // C5
    { midi: 74, duration: "quarter" }, // D5
    { midi: 76, duration: "quarter" }, // E5
    { midi: 77, duration: "quarter" }, // F5
]);

const clef = ref("treble");
const keySignature = ref<string | undefined>(undefined);
const timeSignature = ref("4/4");
const enforceTimeSignature = ref(true);
const showFingerings = ref(true);
const showFingeringLabels = ref(false);
const skipRepeatingFingerings = ref(true);

const staffWidth = ref(800);
const staffHeight = ref(275);
const fingeringWidth = ref(110);
const fingeringHeight = ref(120);

// Decoration options
const highlightIndex = ref(-1);
const incorrectIndex = ref(-1);
const mutedOpacity = ref(0.3);

// Overlay options (pitch feedback)
const overlayEnabled = ref(false);
const overlayNoteIndex = ref(0);
const overlayMidi = ref(74); // D5 by default

// Create overlay object for pitch feedback
const noteOverlay = computed((): NoteOverlay | undefined => {
    if (!overlayEnabled.value || overlayNoteIndex.value < 0 || overlayNoteIndex.value >= phraseNotes.value.length) {
        return undefined;
    }
    return {
        noteIndex: overlayNoteIndex.value,
        playedNote: { midi: overlayMidi.value },
        color: "#F44336", // Red for incorrect
    };
});

// Apply decorations to notes based on settings
const decoratedNotes = computed(() => {
    return phraseNotes.value.map((note, index) => {
        const decorated = { ...note };

        // Apply highlight
        if (index === highlightIndex.value) {
            decorated.highlight = true;
        }

        // Apply incorrect marking
        if (index === incorrectIndex.value) {
            decorated.incorrect = true;
        }

        // Apply muted opacity for past notes
        if (highlightIndex.value >= 0 && index < highlightIndex.value) {
            decorated.opacity = mutedOpacity.value;
        }

        return decorated;
    });
});

const phrasePresets: PhrasePreset[] = [
    {
        name: "C Major Scale",
        notes: [
            { midi: 72, duration: "quarter" }, // C5
            { midi: 74, duration: "quarter" }, // D5
            { midi: 76, duration: "quarter" }, // E5
            { midi: 77, duration: "quarter" }, // F5
            { midi: 79, duration: "quarter" }, // G5
            { midi: 81, duration: "quarter" }, // A5
            { midi: 83, duration: "quarter" }, // B5
            { midi: 84, duration: "quarter" }, // C6
        ],
        timeSignature: "4/4",
    },
    {
        name: "Hot Cross Buns",
        notes: [
            { midi: 76, duration: "quarter" }, // E5
            { midi: 74, duration: "quarter" }, // D5
            { midi: 72, duration: "half" }, // C5
            { midi: 76, duration: "quarter" }, // E5
            { midi: 74, duration: "quarter" }, // D5
            { midi: 72, duration: "half" }, // C5
        ],
        timeSignature: "4/4",
    },
    {
        name: "Mary Had a Little Lamb",
        notes: [
            { midi: 76, duration: "quarter" }, // E5
            { midi: 74, duration: "quarter" }, // D5
            { midi: 72, duration: "quarter" }, // C5
            { midi: 74, duration: "quarter" }, // D5
            { midi: 76, duration: "quarter" }, // E5
            { midi: 76, duration: "quarter" }, // E5
            { midi: 76, duration: "half" }, // E5
        ],
        timeSignature: "4/4",
    },
    {
        name: "Arpeggio (C Major)",
        notes: [
            { midi: 72, duration: "eighth" }, // C5
            { midi: 76, duration: "eighth" }, // E5
            { midi: 79, duration: "eighth" }, // G5
            { midi: 84, duration: "eighth" }, // C6
            { midi: 79, duration: "eighth" }, // G5
            { midi: 76, duration: "eighth" }, // E5
            { midi: 72, duration: "quarter" }, // C5
        ],
        timeSignature: "4/4",
    },
    {
        name: "Repeated Notes Test",
        notes: [
            { midi: 72, duration: "quarter" }, // C5
            { midi: 72, duration: "quarter" }, // C5
            { midi: 74, duration: "quarter" }, // D5
            { midi: 74, duration: "quarter" }, // D5
            { midi: 76, duration: "quarter" }, // E5
            { midi: 76, duration: "quarter" }, // E5
        ],
        timeSignature: "4/4",
    },
    {
        name: "With Accidentals",
        notes: [
            { midi: 72, duration: "quarter" }, // C5
            { midi: 74, duration: "quarter" }, // D5
            { midi: 76, duration: "quarter" }, // E5
            { midi: 78, duration: "quarter" }, // F#5
            { midi: 79, duration: "quarter" }, // G5
            { midi: 82, duration: "quarter", spelling: "flat" }, // Bb5
        ],
        timeSignature: "4/4",
    },
    {
        name: "Playback Demo (Highlighted)",
        notes: [
            { midi: 72, duration: "quarter", opacity: 0.3 }, // C5
            { midi: 74, duration: "quarter", opacity: 0.3 }, // D5
            { midi: 76, duration: "quarter", highlight: true }, // E5
            { midi: 77, duration: "quarter" }, // F5
            { midi: 79, duration: "quarter" }, // G5
            { midi: 81, duration: "quarter" }, // A5
        ],
        timeSignature: "4/4",
    },
    {
        name: "Error Feedback Demo",
        notes: [
            { midi: 72, duration: "quarter" }, // C5
            { midi: 74, duration: "quarter" }, // D5
            { midi: 76, duration: "quarter", incorrect: true }, // E5
            { midi: 77, duration: "quarter" }, // F5
        ],
        timeSignature: "4/4",
    },
    {
        name: "Free Timing: 15 Quarter Notes",
        notes: [
            { midi: 72, duration: "quarter" }, // C5
            { midi: 74, duration: "quarter" }, // D5
            { midi: 76, duration: "quarter" }, // E5
            { midi: 77, duration: "quarter" }, // F5
            { midi: 79, duration: "quarter" }, // G5
            { midi: 81, duration: "quarter" }, // A5
            { midi: 83, duration: "quarter" }, // B5
            { midi: 84, duration: "quarter" }, // C6
            { midi: 83, duration: "quarter" }, // B5
            { midi: 81, duration: "quarter" }, // A5
            { midi: 79, duration: "quarter" }, // G5
            { midi: 77, duration: "quarter" }, // F5
            { midi: 76, duration: "quarter" }, // E5
            { midi: 74, duration: "quarter" }, // D5
            { midi: 72, duration: "quarter" }, // C5
        ],
        timeSignature: "4/4",
        enforceTimeSignature: false,
    },
    {
        name: "Free Timing: Mixed Durations",
        notes: [
            { midi: 72, duration: "whole" }, // C5
            { midi: 74, duration: "half" }, // D5
            { midi: 76, duration: "quarter" }, // E5
            { midi: 77, duration: "eighth" }, // F5
            { midi: 79, duration: "eighth" }, // G5
            { midi: 81, duration: "quarter" }, // A5
            { midi: 83, duration: "half" }, // B5
            { midi: 84, duration: "quarter" }, // C6
            { midi: 86, duration: "sixteenth" }, // D6
            { midi: 88, duration: "sixteenth" }, // E6
            { midi: 89, duration: "sixteenth" }, // F6
            { midi: 91, duration: "sixteenth" }, // G6
        ],
        timeSignature: "4/4",
        enforceTimeSignature: false,
    },
    {
        name: "Free Timing: Chromatic Run",
        notes: [
            { midi: 72, duration: "eighth" }, // C5
            { midi: 73, duration: "eighth" }, // C#5
            { midi: 74, duration: "eighth" }, // D5
            { midi: 75, duration: "eighth" }, // D#5
            { midi: 76, duration: "eighth" }, // E5
            { midi: 77, duration: "eighth" }, // F5
            { midi: 78, duration: "eighth" }, // F#5
            { midi: 79, duration: "eighth" }, // G5
            { midi: 80, duration: "eighth" }, // G#5
            { midi: 81, duration: "eighth" }, // A5
            { midi: 82, duration: "eighth" }, // A#5
            { midi: 83, duration: "eighth" }, // B5
            { midi: 84, duration: "quarter" }, // C6
        ],
        timeSignature: "4/4",
        enforceTimeSignature: false,
    },
];

function updateNoteFromString(index: number, noteStr: string) {
    const midi = noteToMidi(noteStr);
    if (midi !== undefined) {
        phraseNotes.value[index].midi = midi;
    }
}

function addNote() {
    phraseNotes.value.push({ midi: 72, duration: "quarter" }); // C5
}

function removeNote(index: number) {
    if (phraseNotes.value.length > 1) {
        phraseNotes.value.splice(index, 1);
    }
}

function loadPreset(preset: PhrasePreset) {
    phraseNotes.value = [...preset.notes];
    if (preset.timeSignature) {
        timeSignature.value = preset.timeSignature;
    }
    if (preset.enforceTimeSignature !== undefined) {
        enforceTimeSignature.value = preset.enforceTimeSignature;
    } else {
        enforceTimeSignature.value = true; // Default to true for existing presets
    }
    // Reset decorations when loading preset
    highlightIndex.value = -1;
    incorrectIndex.value = -1;
}

const componentCode = computed(() => {
    const notesString = JSON.stringify(phraseNotes.value, null, 2)
        .split("\n")
        .map((line, i) => (i === 0 ? line : "  " + line))
        .join("\n");

    const props: string[] = [];
    props.push(`  :notes="${notesString}"`);

    if (clef.value !== "treble") {
        props.push(`  clef="${clef.value}"`);
    }

    if (keySignature.value !== undefined) {
        props.push(`  key-signature="${keySignature.value}"`);
    }

    if (timeSignature.value !== "4/4") {
        props.push(`  time-signature="${timeSignature.value}"`);
    }

    if (!enforceTimeSignature.value) {
        props.push(`  :enforce-time-signature="false"`);
    }

    if (staffWidth.value !== 800) {
        props.push(`  :width="${staffWidth.value}"`);
    }

    if (staffHeight.value !== 275) {
        props.push(`  :height="${staffHeight.value}"`);
    }

    if (!showFingerings.value) {
        props.push(`  :show-fingerings="false"`);
    } else {
        if (showFingeringLabels.value) {
            props.push(`  :show-fingering-labels="true"`);
        }
        if (!skipRepeatingFingerings.value) {
            props.push(`  :skip-repeating-fingerings="false"`);
        }
        if (fingeringWidth.value !== 110) {
            props.push(`  :fingering-width="${fingeringWidth.value}"`);
        }
        if (fingeringHeight.value !== 120) {
            props.push(`  :fingering-height="${fingeringHeight.value}"`);
        }
    }

    const propsString = props.length > 0 ? "\n" + props.join("\n") + "\n" : "";
    return `<MusicPhrase${propsString}/>`;
});

async function copyCode() {
    try {
        await navigator.clipboard.writeText(componentCode.value);
        alert("Code copied to clipboard!");
    } catch (err) {
        console.error("Failed to copy:", err);
    }
}
</script>

<style scoped>
.debug-container {
    width: 100%;
    margin: 0;
    padding: 20px;
}

h1 {
    padding-bottom: 10px;
    margin-bottom: 30px;
}

h2 {
    font-size: 1.4em;
    margin-bottom: 15px;
}

h3 {
    font-size: 1.1em;
    margin-bottom: 10px;
    margin-top: 0;
    font-weight: 600;
}

.layout {
    display: grid;
    grid-template-columns: 1fr 1.5fr 1fr;
    gap: 20px;
    height: calc(100vh - 200px);
    margin-bottom: 40px;
}

.controls-panel {
    padding: 20px;
    border-radius: 8px;
    overflow-y: auto;
}

.preview-panel {
    padding: 20px;
    border-radius: 8px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
}

.right-panel {
    padding: 20px;
    border-radius: 8px;
    overflow-y: auto;
}

.property-section {
    margin-bottom: 25px;
    padding: 15px;
    border-radius: 6px;
}

.property-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.property-control {
    padding: 12px;
    border-radius: 4px;
}

.property-control label {
    display: block;
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 14px;
}

.checkbox-label {
    display: flex !important;
    align-items: center;
    gap: 8px;
    cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
    margin: 0;
    cursor: pointer;
}

.checkbox-label input[type="checkbox"]:disabled {
    cursor: not-allowed;
}

.notes-editor {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 10px;
}

.note-row {
    display: flex;
    gap: 8px;
    align-items: center;
}

.note-input {
    flex: 1;
    padding: 6px;
    border: 1px solid rgb(200, 200, 200);
    border-radius: 4px;
    font-size: 13px;
}

.midi-input {
    width: 60px;
    padding: 6px;
    border: 1px solid rgb(200, 200, 200);
    border-radius: 4px;
    font-size: 13px;
}

.duration-select {
    padding: 6px;
    border: 1px solid rgb(200, 200, 200);
    border-radius: 4px;
    font-size: 13px;
}

.remove-btn {
    padding: 4px 10px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-weight: bold;
}

.remove-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.add-note-btn {
    width: 100%;
    padding: 8px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
}

.button-group {
    display: flex;
    gap: 5px;
    flex-wrap: wrap;
}

.button-group-vertical {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.state-btn {
    padding: 6px 12px;
    border: 1px solid rgb(200, 200, 200);
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: all 0.2s;
    text-align: center;
    font-weight: 500;
    background: white;
}

.state-btn:hover:not(:disabled) {
    opacity: 0.8;
}

.state-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.state-btn.active {
    font-weight: bold;
    border-color: rgb(33, 150, 243);
    background: rgba(33, 150, 243, 0.05);
}

.preview-box {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px;
    border-radius: 6px;
    flex: 1;
    overflow-x: auto;
}

.size-controls {
    margin-top: 20px;
    padding: 15px;
    border-radius: 6px;
    flex-shrink: 0;
}

.size-controls label {
    display: block;
    margin-bottom: 5px;
    font-weight: 600;
    font-size: 14px;
}

.size-control-group {
    margin-bottom: 20px;
}

.size-controls input[type="range"] {
    width: 100%;
    margin-bottom: 0;
}

.preview-info {
    display: none;
}

.prop-display div {
    margin-bottom: 5px;
    font-family: monospace;
    font-size: 14px;
    padding: 8px;
    border-radius: 3px;
    font-weight: 500;
}

.instructions-section {
    display: none;
}

.usage-list {
    line-height: 1.8;
    padding-left: 20px;
}

.usage-list li {
    margin-bottom: 12px;
}

.demo-tip {
    margin-top: 16px;
    padding: 12px 16px;
    background: rgba(33, 150, 243, 0.1);
    border-left: 4px solid #2196f3;
    border-radius: 4px;
    font-size: 14px;
}

.code-section {
    display: none;
}

.code-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.code-section pre {
    margin: 0;
    overflow-x: auto;
    padding: 15px;
    border-radius: 4px;
}

.code-section code {
    font-family: "Fira Code", "Consolas", monospace;
    font-size: 13px;
    line-height: 1.5;
}

.api-section {
    display: none;
}

.api-list {
    line-height: 1.8;
    padding-left: 20px;
}

.api-list li {
    margin-bottom: 8px;
}

.type-definition,
.usage-example {
    background: rgba(0, 0, 0, 0.05);
    padding: 12px;
    border-radius: 4px;
    overflow-x: auto;
    margin: 8px 0;
}

.type-definition code,
.usage-example code {
    font-family: "Fira Code", "Consolas", monospace;
    font-size: 13px;
    line-height: 1.5;
}

@media (max-width: 1200px) {
    .layout {
        grid-template-columns: 1fr;
    }
}
</style>
